import {
  Avatar,
  Box,
  Card,
  CardBody,
  Flex,
  Heading,
  SimpleGrid,
  Stack,
  Stat,
  StatLabel,
  StatNumber,
  Text,
} from '@chakra-ui/react';
import { formatMoney } from 'common/string.helper';
import { Championnat } from 'features/championnats/championnat.types';
import { Player } from 'features/players/players.types';
import { RoutePath } from 'router/routes.paths';
import { useNavigate } from 'react-router-dom';

type Statistique = {
  label: string;
  value: number;
};

function StatistiqueChampionnat({ label, value }: Statistique) {
  return (
    <Stat>
      <StatLabel color="gray.500" fontSize="xs">
        {label}
      </StatLabel>
      <StatNumber fontSize="md">{value}</StatNumber>
    </Stat>
  );
}

export default function ChampionnatJoueurActifCard({
  championnat,
  user,
}: {
  championnat: Championnat;
  user: Player;
}) {
  const navigate = useNavigate();
  const championnatPlayer = championnat.players.find(
    ({ id }) => id === user.id,
  );
  const position = championnatPlayer
    ? championnat.players.findIndex(({ id }) => id === user.id) + 1
    : undefined;

  return (
    <Card
      cursor="pointer"
      overflow="hidden"
      transition="transform 0.15s ease, box-shadow 0.15s ease"
      _hover={{ transform: 'translateY(-2px)', shadow: 'lg' }}
      onClick={() => navigate(`${RoutePath.championnats}/${championnat.id}`)}
    >
      <Box bgGradient="linear(to-r, orange.500, orange.400)" px="5" py="3">
        <Flex alignItems="center" justifyContent="space-between">
          <Heading color="white" fontSize="md" noOfLines={1}>
            {championnat.name}
          </Heading>
          <Text color="whiteAlpha.900" fontSize="sm">
            {championnat.nbTournois} tournoi
            {championnat.nbTournois > 1 ? 's' : ''}
          </Text>
        </Flex>
      </Box>
      <CardBody p="5">
        {championnatPlayer ? (
          <Stack spacing="5">
            <Flex alignItems="center" justifyContent="space-between">
              <Flex alignItems="center">
                <Avatar name={user.name} src={user.photo} size="md" mr="3" />
                <Box>
                  <Text
                    color="gray.500"
                    fontSize="xs"
                    textTransform="uppercase"
                  >
                    Votre classement
                  </Text>
                  <Heading size="lg">
                    {position}
                    <Text
                      as="span"
                      color="gray.500"
                      fontSize="md"
                      fontWeight="normal"
                    >
                      {' '}
                      / {championnat.players.length}
                    </Text>
                  </Heading>
                </Box>
              </Flex>
              <Stat textAlign="right">
                <StatLabel color="gray.500" fontSize="xs">
                  Points
                </StatLabel>
                <StatNumber color="orange.500" fontSize="2xl">
                  {championnatPlayer.points}
                </StatNumber>
              </Stat>
            </Flex>

            <SimpleGrid columns={3} spacing="3">
              <Stat>
                <StatLabel color="gray.500" fontSize="xs">
                  Dépenses
                </StatLabel>
                <StatNumber fontSize="sm">
                  {formatMoney(championnatPlayer.totalDepense)}
                </StatNumber>
              </Stat>
              <Stat>
                <StatLabel color="gray.500" fontSize="xs">
                  Gains
                </StatLabel>
                <StatNumber fontSize="sm">
                  {formatMoney(championnatPlayer.totalGain)}
                </StatNumber>
              </Stat>
              <Stat>
                <StatLabel color="gray.500" fontSize="xs">
                  Bénéfices
                </StatLabel>
                <StatNumber
                  color={
                    championnatPlayer.totalGain -
                      championnatPlayer.totalDepense >=
                    0
                      ? 'green.500'
                      : 'red.500'
                  }
                  fontSize="sm"
                >
                  {formatMoney(
                    championnatPlayer.totalGain -
                      championnatPlayer.totalDepense,
                  )}
                </StatNumber>
              </Stat>
            </SimpleGrid>

            <SimpleGrid
              borderTop="1px"
              borderColor="gray.100"
              columns={4}
              pt="4"
              spacing="3"
            >
              <StatistiqueChampionnat
                label="ITM"
                value={championnatPlayer.nbItm}
              />
              <StatistiqueChampionnat
                label="Victoires"
                value={championnatPlayer.nbVictoires}
              />
              <StatistiqueChampionnat
                label="Kills"
                value={championnatPlayer.totalKill}
              />
              <StatistiqueChampionnat
                label="Tournois"
                value={championnatPlayer.nbTournoiPlayed}
              />
            </SimpleGrid>
          </Stack>
        ) : (
          <Flex alignItems="center" minH="144px">
            <Avatar name={user.name} src={user.photo} mr="3" />
            <Box>
              <Heading fontSize="md">Pas encore de résultat</Heading>
              <Text color="gray.500" fontSize="sm">
                Vos statistiques apparaîtront après votre premier tournoi.
              </Text>
            </Box>
          </Flex>
        )}
      </CardBody>
    </Card>
  );
}
